"use client"

import { useState, useEffect } from "react"
import { Info, Filter, Loader2, Upload } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"
import { Badge } from "@/components/ui/badge"
import { OverviewTab } from "@/components/overview-tab"
import { TranscriptionTab } from "@/components/transcription-tab"
import { CriteriaTab } from "@/components/criteria-tab"
import { FeedbackTab } from "@/components/feedback-tab"
import { UploadTab } from "@/components/upload-tab"
import { AudioPlayer } from "@/components/audio-player"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Dialog, DialogContent, DialogTrigger, DialogTitle } from "@/components/ui/dialog"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { api, API_BASE_URL } from "@/lib/api"

interface Conversation {
  id: string
  convId: string
  audioId: string // Full audio_id for API calls
  owner: string
  date: string
  time: string
  duration: string
  version: string
  status: "PASS" | "FAIL"
  source: "Human Agent" | "Simulation Agent" | "Web Agent"
}

// Helper function to format duration from seconds to MM:SS
function formatDuration(seconds?: number | null): string {
  if (!seconds || seconds === 0) {
    return "N/A"
  }
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, "0")}`
}

interface FilterState {
  date: string[]
  owner: string[]
  duration: string[]
  criteria: string[]
  version: string[]
  status: string[]
  source: string[]
}

export function EvalsAnalysis() {
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null)
  const [audioResult, setAudioResult] = useState<any>(null)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [activeTab, setActiveTab] = useState("overview")
  const [searchQuery, setSearchQuery] = useState("")
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<FilterState>({
    date: [],
    owner: [],
    duration: [],
    criteria: [],
    version: [],
    status: [],
    source: [],
  })
  const [availableOwners, setAvailableOwners] = useState<string[]>([])
  const [availableVersions, setAvailableVersions] = useState<string[]>([])
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false)

  // Fetch conversations from API
  useEffect(() => {
    const fetchConversations = async () => {
      setLoading(true)
      setError(null)
      try {
        console.log("[EvalsAnalysis] Fetching conversations history...")
        const response = await api.getHistory({
          limit: 100,
          offset: 0,
          status_filter: filters.status.length > 0 ? filters.status[0] : undefined,
        })

        console.log("[EvalsAnalysis] API Response:", {
          total: response.total,
          conversationsCount: response.conversations.length,
          conversations: response.conversations.slice(0, 3), // Log first 3 for debugging
        })

        // Convert API response to Conversation format
        const mappedConversations: Conversation[] = response.conversations.map((conv) => {
          const audioIdStr = typeof conv.audio_id === 'string' ? conv.audio_id : String(conv.audio_id)
          const createdDate = new Date(conv.created_at)
          
          return {
            id: conv.id,
            convId: audioIdStr.length > 30 ? audioIdStr.substring(0, 30) + "..." : audioIdStr,
            audioId: audioIdStr, // Store full audio_id
            owner: conv.eval_suite_id || "N/A",
            date: createdDate.toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            }),
            time: createdDate.toLocaleTimeString("en-US", {
              hour: "numeric",
              minute: "2-digit",
            }),
            duration: formatDuration(conv.duration),
            version: conv.agent_version || "N/A",
            status: conv.status === "PASS" ? "PASS" : "FAIL",
            source: (conv as any).source || "Human Agent", // Default to "Human Agent" if not provided
          }
        })

        setConversations(mappedConversations)
        console.log("[EvalsAnalysis] Mapped conversations:", mappedConversations.slice(0, 3))
        
        // Set selected conversation to first one if available
        if (mappedConversations.length > 0 && !selectedConversation) {
          setSelectedConversation(mappedConversations[0])
          console.log("[EvalsAnalysis] Auto-selected first conversation:", mappedConversations[0].id)
        }

        // Extract unique owners and versions for filters
        const ownersSet = new Set<string>()
        const versionsSet = new Set<string>()
        response.conversations.forEach((conv) => {
          if (conv.eval_suite_id) ownersSet.add(conv.eval_suite_id)
          if (conv.agent_version) versionsSet.add(conv.agent_version)
        })
        setAvailableOwners(Array.from(ownersSet))
        setAvailableVersions(Array.from(versionsSet))
      } catch (err: any) {
        console.error("Failed to fetch conversations:", err)
        setError(err.message || "Failed to load conversations")
      } finally {
        setLoading(false)
      }
    }

    fetchConversations()
  }, []) // Only fetch on mount

  // Fetch conversation detail when selected conversation changes
  useEffect(() => {
    const fetchConversationDetail = async () => {
      if (!selectedConversation) {
        setAudioResult(null)
        return
      }

      setLoadingDetail(true)
      try {
        console.log("[EvalsAnalysis] Fetching conversation detail for ID:", selectedConversation.id)
        const detail = await api.getConversationDetail(selectedConversation.id)
        console.log("[EvalsAnalysis] Conversation detail received:", {
          audio_id: detail.audio_id,
          verdict: detail.verdict,
          overall_score: detail.overall_score,
          transcript_length: detail.transcript?.length || 0,
          scenario_details_count: detail.scenario_details?.length || 0,
        })
        
        // Convert to AudioResult format for compatibility with existing components
        const audioIdStr = typeof detail.audio_id === 'string' ? detail.audio_id : String(detail.audio_id)
        const audioData = {
          audio_id: audioIdStr,
          audio_url: `${API_BASE_URL}/batch/audio/${audioIdStr}`,
          overall_score: detail.overall_score,
          verdict: detail.verdict,
          transcript: detail.transcript || undefined,
          scenario_details: detail.scenario_details || undefined,
          duration_sec: detail.duration_sec || undefined,
          user_feedback: detail.feedback || undefined,
          user_feedback_updated_at: undefined,
          user_feedback_updated_by: undefined,
        }
        
        setAudioResult(audioData)
      } catch (err: any) {
        console.error("Failed to fetch conversation detail:", err)
        setError(err.message || "Failed to load conversation details")
      } finally {
        setLoadingDetail(false)
      }
    }

    fetchConversationDetail()
  }, [selectedConversation])

  const filteredConversations = conversations.filter((conversation) => {
    const matchesSearch = conversation.convId.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesOwner = filters.owner.length === 0 || filters.owner.includes(conversation.owner)
    const matchesVersion = filters.version.length === 0 || filters.version.includes(conversation.version)
    const matchesStatus = filters.status.length === 0 || filters.status.includes(conversation.status)
    const matchesSource = filters.source.length === 0 || filters.source.includes(conversation.source)
    return matchesSearch && matchesOwner && matchesVersion && matchesStatus && matchesSource
  })

  const toggleFilter = (category: keyof FilterState, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [category]: prev[category].includes(value)
        ? prev[category].filter((v) => v !== value)
        : [...prev[category], value],
    }))
  }

  return (
    <div className="flex h-full flex-col">
      {/* Analysis Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Conversations List */}
        <div className="w-[360px] flex-shrink-0 border-r border-border flex flex-col bg-white overflow-hidden">
          <div className="border-b border-border p-4">
          <div className="flex gap-2">
            <Input
              placeholder="Search conversations..."
              className="h-9 flex-1"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="h-9 gap-2 text-xs bg-transparent flex-shrink-0">
                  <Upload className="h-3.5 w-3.5" />
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogTitle className="sr-only">Upload Call Recordings</DialogTitle>
                <UploadTab />
              </DialogContent>
            </Dialog>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-9 gap-2 text-xs bg-transparent flex-shrink-0">
                  <Filter className="h-3.5 w-3.5" />
                  {filters.owner.length + filters.version.length + filters.status.length + filters.source.length > 0 && (
                    <Badge
                      variant="secondary"
                      className="ml-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs"
                    >
                      {filters.owner.length + filters.version.length + filters.status.length + filters.source.length}
                    </Badge>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 max-h-[400px] overflow-y-auto" align="start">
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium mb-2 text-sm">Owner</h4>
                    <div className="space-y-2">
                      {availableOwners.length === 0 ? (
                        <p className="text-xs text-muted-foreground">No owners available</p>
                      ) : (
                        availableOwners.map((owner) => (
                          <div key={owner} className="flex items-center space-x-2">
                            <Checkbox
                              id={`owner-${owner}`}
                              checked={filters.owner.includes(owner)}
                              onCheckedChange={() => toggleFilter("owner", owner)}
                            />
                            <Label htmlFor={`owner-${owner}`} className="text-sm font-normal cursor-pointer">
                              {owner}
                            </Label>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2 text-sm">Version</h4>
                    <div className="space-y-2">
                      {availableVersions.length === 0 ? (
                        <p className="text-xs text-muted-foreground">No versions available</p>
                      ) : (
                        availableVersions.map((version) => (
                          <div key={version} className="flex items-center space-x-2">
                            <Checkbox
                              id={`version-${version}`}
                              checked={filters.version.includes(version)}
                              onCheckedChange={() => toggleFilter("version", version)}
                            />
                            <Label htmlFor={`version-${version}`} className="text-sm font-normal cursor-pointer">
                              {version}
                            </Label>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2 text-sm">Status</h4>
                    <div className="space-y-2">
                      {["PASS", "FAIL"].map((status) => (
                        <div key={status} className="flex items-center space-x-2">
                          <Checkbox
                            id={`status-${status}`}
                            checked={filters.status.includes(status)}
                            onCheckedChange={() => toggleFilter("status", status)}
                          />
                          <Label htmlFor={`status-${status}`} className="text-sm font-normal cursor-pointer">
                            {status}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2 text-sm">Source</h4>
                    <div className="space-y-2">
                      {["Human Agent", "Simulation Agent", "Web Agent"].map((source) => (
                        <div key={source} className="flex items-center space-x-2">
                          <Checkbox
                            id={`source-${source}`}
                            checked={filters.source.includes(source)}
                            onCheckedChange={() => toggleFilter("source", source)}
                          />
                          <Label htmlFor={`source-${source}`} className="text-sm font-normal cursor-pointer">
                            {source}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full bg-transparent"
                    onClick={() =>
                      setFilters({ date: [], owner: [], duration: [], criteria: [], version: [], status: [], source: [] })
                    }
                  >
                    Clear All Filters
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </div>
          </div>

          <div className="flex-1 overflow-y-auto min-h-0">
          {loading ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center p-8 text-center">
              <p className="text-sm text-muted-foreground mb-2">Error loading conversations</p>
              <p className="text-xs text-muted-foreground">{error}</p>
            </div>
          ) : filteredConversations.length === 0 ? (
            <div className="flex items-center justify-center p-8">
              <p className="text-sm text-muted-foreground">No conversations found</p>
            </div>
          ) : (
            filteredConversations.map((conversation) => (
              <button
                key={conversation.id}
                onClick={() => setSelectedConversation(conversation)}
                className={`w-full border-b border-border p-4 text-left hover:bg-gray-50 transition-colors ${
                  selectedConversation?.id === conversation.id ? "bg-gray-50" : ""
                }`}
              >
              <div className="flex items-start gap-2 mb-2">
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 15 15"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="mt-0.5 flex-shrink-0"
                >
                  <path
                    d="M6.1584 3.13508C6.35985 2.94621 6.67627 2.95642 6.86514 3.15788L10.6151 7.15788C10.7954 7.3502 10.7954 7.64949 10.6151 7.84182L6.86514 11.8418C6.67627 12.0433 6.35985 12.0535 6.1584 11.8646C5.95694 11.6757 5.94673 11.3593 6.1356 11.1579L9.565 7.49985L6.1356 3.84182C5.94673 3.64036 5.95694 3.32394 6.1584 3.13508Z"
                    fill="currentColor"
                    fillRule="evenodd"
                    clipRule="evenodd"
                  />
                </svg>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium mb-0.5">
                        {conversation.date}, {conversation.time}
                      </div>
                      <div className="text-xs text-muted-foreground font-mono truncate">{conversation.convId}</div>
                    </div>
                    <Badge
                      variant={conversation.status === "PASS" ? "default" : "destructive"}
                      className={`text-xs flex-shrink-0 ${
                        conversation.status === "PASS"
                          ? "bg-green-100 text-green-800 hover:bg-green-100"
                          : "bg-red-100 text-red-800 hover:bg-red-100"
                      }`}
                    >
                      {conversation.status}
                    </Badge>
                  </div>
                  <div className="text-xs text-muted-foreground space-y-0.5">
                    <div>
                      <span className="font-medium">Owner:</span> {conversation.owner}
                    </div>
                    <div>
                      <span className="font-medium">Duration:</span> {conversation.duration}
                    </div>
                    <div>
                      <span className="font-medium">Version:</span> {conversation.version}
                    </div>
                  </div>
                </div>
              </div>
            </button>
            ))
          )}
          </div>
        </div>

        {/* Right Panel - Conversation Details */}
        {selectedConversation ? (
          <div className="flex-1 flex flex-col bg-white">
            <div className="border-b border-border px-6 py-4">
              <h2 className="font-semibold text-xl mb-1">Conversation Details</h2>
              <p className="text-sm text-muted-foreground font-mono">{selectedConversation.convId}</p>
            </div>

            <div className="border-b border-border px-6 py-6">
              {audioResult && selectedConversation && (
                <AudioPlayer 
                  audioUrl={audioResult.audio_url} 
                  audioId={audioResult.audio_id} 
                />
              )}
            </div>

            <div className="px-6 py-4 bg-blue-50 border-b border-blue-200">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-900">
                  You can now ensure your agent returns high quality responses to conversations like this one. Try Tests in
                  the Transcription tab.
                </p>
              </div>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
          <div className="border-b border-border px-6">
            <TabsList className="h-12 bg-transparent p-0 gap-8">
                <TabsTrigger
                  value="overview"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-black data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 font-medium"
                >
                  Overview
                </TabsTrigger>
                <TabsTrigger
                  value="transcription"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-black data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 font-medium"
                >
                  Transcription
                </TabsTrigger>
                <TabsTrigger
                  value="criteria"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-black data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 font-medium"
                >
                  All Eval Criteria
                </TabsTrigger>
                <TabsTrigger
                  value="feedback"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-black data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 font-medium"
                >
                  User Feedback
                </TabsTrigger>
              </TabsList>
            </div>

            <div className="flex-1 overflow-auto">
              {loadingDetail ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <>
                  <TabsContent value="overview" className="h-full m-0 p-6">
                    <OverviewTab audioResult={audioResult} />
                  </TabsContent>
                  <TabsContent value="transcription" className="h-full m-0 p-6">
                    <TranscriptionTab audioResult={audioResult} />
                  </TabsContent>
                  <TabsContent value="criteria" className="h-full m-0 p-6">
                    <CriteriaTab audioResult={audioResult} />
                  </TabsContent>
                  <TabsContent value="feedback" className="h-full m-0 p-6">
                    <FeedbackTab 
                      audioResult={audioResult}
                      onFeedbackSaved={(updatedResult) => {
                        // Use the updated result directly from the API response
                        if (updatedResult) {
                          const audioIdStr = typeof updatedResult.audio_id === 'string' 
                            ? updatedResult.audio_id 
                            : String(updatedResult.audio_id)
                          const audioData = {
                            audio_id: audioIdStr,
                            audio_url: updatedResult.audio_url || `${API_BASE_URL}/batch/audio/${audioIdStr}`,
                            overall_score: updatedResult.overall_score,
                            verdict: updatedResult.verdict,
                            transcript: updatedResult.transcript || undefined,
                            scenario_details: updatedResult.scenario_details || undefined,
                            duration_sec: updatedResult.duration_sec || undefined,
                            user_feedback: updatedResult.user_feedback || undefined,
                            user_feedback_updated_at: updatedResult.user_feedback_updated_at || undefined,
                            user_feedback_updated_by: updatedResult.user_feedback_updated_by || undefined,
                          }
                          setAudioResult(audioData)
                        }
                      }}
                    />
                  </TabsContent>
                </>
              )}
            </div>
          </Tabs>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-white">
            <p className="text-sm text-muted-foreground">Select a conversation to view details</p>
          </div>
        )}
      </div>
    </div>
  )
}
